import { useState, useEffect, useCallback, useMemo, type ChangeEvent, type ReactNode } from 'react';
import { Button, Modal, Input, Select, Spin, Switch } from 'antd';
import {
  PlusOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTaskStore } from '../stores/useTaskStore';
import { useChannelMemberStore, useChannelStore } from '../stores';
import { channelDisplayName } from '../lib/channelDisplay';
import { UserAvatar } from './UserAvatar';
import { UserName } from './UserName';
import TasksBoardView from './TasksBoardView';
import TasksTableView from './TasksTableView';
import { TaskPropertiesEditor } from './TaskPropertiesEditor';
import { STATUS_CONFIG, STATUS_COLUMNS } from '../lib/taskStatus';
import {
  HEARTBEAT_INTERVAL_OPTIONS,
  DEFAULT_HEARTBEAT_MINUTES,
  loadPreferredHeartbeatMinutes,
  rememberHeartbeatMinutes,
} from '../lib/taskHeartbeat';
import type { ChannelMember } from '../lib/api';
import * as api from '../lib/api';
import { routes } from '../lib/routes';

const { TextArea } = Input;
const EMPTY_MEMBERS: ChannelMember[] = [];
type WorkspaceSource = 'default' | 'repository_mapping';

const memberOptions = (members: ChannelMember[]) =>
  members.map((member) => ({
    value: member.user_id,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserAvatar userId={member.user_id} displayName={member.display_name} size={20} />
        <UserName displayName={member.display_name} role={member.role} />
      </div>
    ),
  }));

function repositoryMappingError(error: unknown) {
  if (!(error instanceof api.ApiError)) {
    return 'Could not save the task. Your inputs are still here; retry when ready.';
  }
  if (error.code === 'repository_mapping_not_found') {
    return 'Repository mapping is missing. Clone the repository on the selected computer, bind that directory in Computers, then retry.';
  }
  if (error.code === 'repository_computer_offline') {
    return 'The selected computer is offline. Start Remote Helper, then retry.';
  }
  if (error.code === 'repository_mapping_unsupported') {
    return 'Update Remote Helper on the selected computer to enable repository mappings.';
  }
  return error.message;
}

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError';
}

function useRepositoryExecutionOptions(
  channelId: string,
  assigneeId: string | undefined,
  enabled = true,
) {
  const [capability, setCapability] = useState<api.RepositoryExecutionCapability | null>(null);
  const [capabilityAssigneeId, setCapabilityAssigneeId] = useState<string>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setCapability(null);
    setCapabilityAssigneeId(undefined);
    setLoading(false);
    if (!enabled || !assigneeId) return;

    const controller = new AbortController();
    let current = true;
    setLoading(true);
    void api.fetchRepositoryExecutionCapability(channelId, assigneeId, controller.signal).then(
      (nextCapability) => {
        if (!current) return;
        setCapability(nextCapability);
        setCapabilityAssigneeId(assigneeId);
        setLoading(false);
      },
      (error) => {
        if (!current || isAbortError(error)) return;
        setCapability({
          supported: false,
          online: false,
          reason: 'repository_capability_unavailable',
          repositories: [],
        });
        setCapabilityAssigneeId(assigneeId);
        setLoading(false);
      },
    );
    return () => {
      current = false;
      controller.abort();
    };
  }, [assigneeId, channelId, enabled]);

  return {
    capability: capabilityAssigneeId === assigneeId ? capability : null,
    loading,
  };
}

function repositoryCapabilityMessage(capability: api.RepositoryExecutionCapability) {
  switch (capability.reason) {
    case 'repository_computer_not_owned':
      return 'Repository mappings are available only to the owner of the selected computer.';
    case 'repository_computer_offline':
      return 'Repository execution is unavailable while the selected computer is offline.';
    case 'repository_mapping_unsupported':
      return 'Update Remote Helper on the selected computer to enable repository mappings.';
    case 'repository_mapping_failed':
      return 'Could not load repository mappings from the selected computer. Check Remote Helper, then retry.';
    default:
      return 'This assignee is not linked to a repository-capable managed computer.';
  }
}

function RepositoryWorkspaceSelector({
  workspaceSource,
  onWorkspaceSourceChange,
  repository,
  onRepositoryChange,
  capability,
  loading,
  repositoryMapped = false,
  selectionAllowed = true,
}: {
  workspaceSource: WorkspaceSource;
  onWorkspaceSourceChange: (value: WorkspaceSource) => void;
  repository: string;
  onRepositoryChange: (value: string) => void;
  capability: api.RepositoryExecutionCapability | null;
  loading: boolean;
  repositoryMapped?: boolean;
  selectionAllowed?: boolean;
}) {
  const repositories = repositoryMapped
    ? repository
      ? [repository]
      : []
    : (capability?.repositories ?? []);
  const repositoryOptions = repositories.map((identity) => ({
    value: identity,
    label: identity,
  }));
  const repositorySelectionDisabled =
    repositoryMapped ||
    loading ||
    !capability?.supported ||
    repositoryOptions.length === 0 ||
    !selectionAllowed;

  return (
    <>
      <Select
        aria-label="Execution Workspace"
        style={{ width: '100%' }}
        value={workspaceSource}
        onChange={onWorkspaceSourceChange}
        loading={loading}
        disabled={repositoryMapped || loading}
        options={[
          { value: 'default', label: 'Default execution' },
          {
            value: 'repository_mapping',
            label: 'GitHub Repository on selected computer',
            disabled:
              !repositoryMapped &&
              (!capability?.supported || !!capability.reason || !selectionAllowed),
          },
        ]}
      />
      {!repositoryMapped && loading && (
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          Loading repository capability and mappings from the selected computer...
        </div>
      )}
      {!repositoryMapped && !loading && capability && capability.reason && (
        <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
          {repositoryCapabilityMessage(capability)}
        </div>
      )}
      {workspaceSource === 'repository_mapping' && (
        <div style={{ marginTop: 8 }}>
          <Select
            aria-label="GitHub Repository"
            style={{ width: '100%' }}
            value={repository || undefined}
            onChange={onRepositoryChange}
            options={repositoryOptions}
            loading={loading}
            disabled={repositorySelectionDisabled}
            placeholder={loading ? 'Loading repository mappings...' : 'Select a repository'}
          />
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            {repositoryMapped
              ? 'Repository and selected computer are fixed after mapping.'
              : capability?.supported && !capability.reason && capability.repositories.length === 0
                ? 'No repository mappings found. Go to Computers, clone the repository on that computer, then bind its directory.'
                : 'Repository mappings are managed in Computers. Borgee never clones or pulls repositories.'}
          </div>
        </div>
      )}
    </>
  );
}

interface Props {
  channelId?: string;
  guildId?: string;
  /** Personal inbox; the server owns its cross-Server authorization filter. */
  personal?: boolean;
  embedded?: boolean;
  /** Public-channel readers may inspect tasks but cannot create or modify them. */
  readOnly?: boolean;
  /** A thread selected by a surrounding task surface, such as Server Tasks. */
  activeThreadId?: string;
  onTaskClick?: (task: api.Task) => void;
}

export default function TasksPanel({
  channelId,
  guildId,
  personal,
  embedded,
  readOnly = false,
  activeThreadId: selectedThreadId,
  onTaskClick,
}: Props) {
  const navigate = useNavigate();
  const { threadId: routeThreadId } = useParams<{ threadId?: string }>();
  const activeThreadId = selectedThreadId ?? routeThreadId;
  const byTaskId = useTaskStore((s) => s.byTaskId);
  const channels = useChannelStore((s) => s.channels);
  const channelMembersVersion = useChannelStore((s) =>
    channelId ? (s.channelMembersVersion[channelId] ?? 0) : 0,
  );
  const channelMembers = useChannelMemberStore((s) =>
    channelId ? (s.byChannelId[channelId] ?? EMPTY_MEMBERS) : EMPTY_MEMBERS,
  );
  const loadChannelMembers = useChannelMemberStore((s) => s.loadChannelMembers);
  const headerChannel = channelId ? channels.find((c) => c.id === channelId) : null;
  const headerTitle = headerChannel ? `# ${channelDisplayName(headerChannel)}` : personal ? 'My Tasks' : 'All';
  const [guildMembers, setGuildMembers] = useState<ChannelMember[]>([]);
  const members = channelId ? channelMembers : guildMembers;
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTask, setEditingTask] = useState<api.Task | null>(null);
  const [channelMap, setChannelMap] = useState<Record<string, string>>({});
  const [filterCreator, setFilterCreator] = useState<string | undefined>();
  const [filterAssignee, setFilterAssignee] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [viewMode, setViewMode] = useState<'board' | 'table'>(() => {
    try {
      const stored = localStorage.getItem('borgee:tasks:viewMode');
      return stored === 'board' || stored === 'table' ? stored : 'board';
    } catch {
      return 'board';
    }
  });

  const openTaskConversation = useCallback((task: api.Task) => {
    if (task.thread_id) navigate(routes.thread(task.guild_id, task.channel_id, task.thread_id));
  }, [navigate]);

  const toggleViewMode = useCallback((mode: 'board' | 'table') => {
    setViewMode(mode);
    try {
      localStorage.setItem('borgee:tasks:viewMode', mode);
    } catch {}
  }, []);

  const tasks = useMemo(() => {
    const all = Object.values(byTaskId);
    const filtered = channelId
      ? all.filter((t) => t.channel_id === channelId)
      : guildId
        ? all.filter((t) => t.guild_id === guildId)
        : personal
          ? all
          : [];
    return filtered.sort((a, b) => b.created_at - a.created_at);
  }, [byTaskId, channelId, guildId, personal]);

  const loadTasks = useCallback(async () => {
    try {
      if (channelId) {
        await useTaskStore.getState().loadChannelTasks(channelId);
      } else if (guildId) {
        await useTaskStore.getState().loadGuildTasks(guildId);
      } else if (personal) {
        await useTaskStore.getState().loadMyTasks();
      }
    } catch (err) {
      if (!(err instanceof api.ApiError && err.status === 404)) throw err;
    } finally {
      setLoading(false);
    }
  }, [channelId, guildId, personal]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    let stale = false;
    if (channelId) {
      void loadChannelMembers(channelId, channelMembersVersion).catch(() => {});
    } else if (guildId) {
      setGuildMembers([]);
      setChannelMap({});
      api
        .fetchGuildMembers(guildId)
        .then((members) => {
          if (!stale) setGuildMembers(members);
        })
        .catch(() => {});
      api
        .fetchGuildChannels(guildId)
        .then(({ channels }) => {
          const map: Record<string, string> = {};
          for (const ch of channels) map[ch.id] = ch.name;
          if (!stale) setChannelMap(map);
        })
        .catch(() => {});
    } else if (personal) {
      // /me/tasks is a server-owned read model: task source and people are
      // embedded in its authorized response, so this view issues no follow-up
      // per-Server requests just to render rows.
      setGuildMembers([]);
    }
    return () => {
      stale = true;
    };
  }, [channelId, guildId, channelMembersVersion, loadChannelMembers, personal]);

  const filteredTasks = tasks.filter((t) => {
    if (filterCreator && t.created_by !== filterCreator) return false;
    if (filterAssignee && t.assignee_id !== filterAssignee) return false;
    if (filterStatus && t.status !== filterStatus) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="tasks-panel-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="tasks-panel">
      <div className="tasks-panel-header">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!embedded && <span className="tasks-panel-title">{headerTitle}</span>}
          <Select
            size="small"
            placeholder="Creator"
            allowClear
            style={{ width: 140 }}
            value={filterCreator}
            onChange={setFilterCreator}
            options={members.map((m) => ({ value: m.user_id, label: m.display_name }))}
          />
          <Select
            size="small"
            placeholder="Assignee"
            allowClear
            style={{ width: 140 }}
            value={filterAssignee}
            onChange={setFilterAssignee}
            options={members.map((m) => ({ value: m.user_id, label: m.display_name }))}
          />
          <Select
            size="small"
            placeholder="Status"
            allowClear
            style={{ width: 130 }}
            value={filterStatus}
            onChange={setFilterStatus}
            options={STATUS_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
          />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {channelId && !readOnly && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="small"
              onClick={() => setShowCreate(true)}
            >
              New Task
            </Button>
          )}
          <div className="tasks-view-toggle">
            <button
              type="button"
              className={`tasks-view-btn${viewMode === 'board' ? ' active' : ''}`}
              onClick={() => toggleViewMode('board')}
              aria-label="Board view"
            >
              <AppstoreOutlined />
            </button>
            <button
              type="button"
              className={`tasks-view-btn${viewMode === 'table' ? ' active' : ''}`}
              onClick={() => toggleViewMode('table')}
              aria-label="Table view"
            >
              <UnorderedListOutlined />
            </button>
          </div>
        </div>
      </div>
      {viewMode === 'board' ? (
        <TasksBoardView
          tasks={filteredTasks}
          members={members}
          channelMap={channelMap}
          channelId={channelId}
          activeThreadId={activeThreadId}
          onTaskClick={onTaskClick}
          onTaskTitleClick={openTaskConversation}
          onEditTask={readOnly ? undefined : setEditingTask}
          readOnly={readOnly}
        />
      ) : (
        <TasksTableView
          tasks={filteredTasks}
          members={members}
          channelMap={channelMap}
          channelId={channelId}
          activeThreadId={activeThreadId}
          onTaskClick={onTaskClick}
          onTaskTitleClick={openTaskConversation}
          onChannelClick={(task) =>
            task.channel && navigate(routes.channel(task.channel.guild_id, task.channel.channel_id))
          }
          onEditTask={readOnly ? undefined : setEditingTask}
        />
      )}
      {showCreate && channelId && (
        <CreateTaskModal
          channelId={channelId}
          members={members}
          onCreated={() => {
            setShowCreate(false);
            loadTasks();
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}
      {editingTask && (
        <EditTaskModal
          task={editingTask}
          members={members}
          onSaved={() => {
            setEditingTask(null);
            loadTasks();
          }}
          onPropertiesChanged={loadTasks}
          onCancel={() => setEditingTask(null)}
        />
      )}
    </div>
  );
}

export function CreateTaskModal({
  channelId,
  members,
  onCreated,
  onCancel,
}: {
  channelId: string;
  members: ChannelMember[];
  onCreated: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | undefined>();
  const [heartbeatEnabled, setHeartbeatEnabled] = useState(true);
  const [heartbeatMinutes, setHeartbeatMinutes] = useState<number>(loadPreferredHeartbeatMinutes);
  const [heartbeatPrompt, setHeartbeatPrompt] = useState('');
  const [creating, setCreating] = useState(false);
  const [workspaceSource, setWorkspaceSource] = useState<'default' | 'repository_mapping'>(
    'default',
  );
  const [repository, setRepository] = useState('');
  const [createError, setCreateError] = useState<string | null>(null);
  const assigneeOptions = useMemo(() => memberOptions(members), [members]);
  const { capability: repositoryCapability, loading: repositoryCapabilityLoading } =
    useRepositoryExecutionOptions(channelId, assigneeId);
  const repositorySelectionValid =
    !!repositoryCapability?.supported && repositoryCapability.repositories.includes(repository);

  useEffect(() => {
    setWorkspaceSource('default');
    setRepository('');
    setCreateError(null);
  }, [assigneeId]);

  const handleCreate = async () => {
    if (!title.trim() || creating) return;
    if (workspaceSource === 'repository_mapping' && !repositorySelectionValid) return;
    setCreating(true);
    setCreateError(null);
    try {
      await api.createTask(channelId, {
        title: title.trim(),
        description: description.trim(),
        assignee_id: assigneeId,
        heartbeat_interval_ms: heartbeatEnabled ? heartbeatMinutes * 60 * 1000 : undefined,
        heartbeat_prompt: heartbeatPrompt.trim() || undefined,
        heartbeat_enabled: heartbeatEnabled,
        execution:
          workspaceSource === 'repository_mapping'
            ? { workspace_source: 'repository_mapping', repository: repository.trim() }
            : undefined,
      });
      rememberHeartbeatMinutes(heartbeatMinutes);
      onCreated();
    } catch (error) {
      if (error instanceof api.ApiError) {
        if (error.code === 'repository_mapping_not_found') {
          setCreateError(
            'Repository mapping is missing. Clone the repository on the selected computer, bind that directory in Computers, then retry.',
          );
        } else if (error.code === 'repository_computer_offline') {
          setCreateError('The selected computer is offline. Start Remote Helper, then retry.');
        } else {
          setCreateError(error.message);
        }
      } else {
        setCreateError('Could not create the task. Your inputs are still here; retry when ready.');
      }
      setCreating(false);
    }
  };

  return (
    <Modal
      title="New Task"
      open
      onOk={handleCreate}
      onCancel={onCancel}
      okText={creating ? 'Creating...' : 'Create'}
      okButtonProps={{
        disabled:
          !title.trim() ||
          creating ||
          (workspaceSource === 'repository_mapping' &&
            (repositoryCapabilityLoading || !repositorySelectionValid)),
      }}
      width={520}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Title</div>
        <Input
          value={title}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setTitle(event.target.value)}
          autoFocus
          onPressEnter={handleCreate}
          placeholder="Task title"
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Description</div>
        <TextArea
          value={description}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setDescription(event.target.value)}
          rows={3}
          placeholder="Optional description"
        />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Assignee</div>
        <Select
          aria-label="Select assignee"
          style={{ width: '100%' }}
          placeholder="Select assignee"
          allowClear
          value={assigneeId}
          onChange={setAssigneeId}
          options={assigneeOptions}
        />
      </div>
      {assigneeId && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Execution Workspace</div>
          <RepositoryWorkspaceSelector
            workspaceSource={workspaceSource}
            onWorkspaceSourceChange={setWorkspaceSource}
            repository={repository}
            onRepositoryChange={setRepository}
            capability={repositoryCapability}
            loading={repositoryCapabilityLoading}
          />
        </div>
      )}
      {createError && (
        <div role="alert" style={{ color: '#ff4d4f', fontSize: 12, marginTop: 12 }}>
          {createError}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Heartbeat</span>
          <Switch size="small" checked={heartbeatEnabled} onChange={setHeartbeatEnabled} />
        </div>
        {heartbeatEnabled && (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={heartbeatMinutes}
            onChange={setHeartbeatMinutes}
            options={HEARTBEAT_INTERVAL_OPTIONS}
          />
        )}
        {heartbeatEnabled && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 12, color: '#888', cursor: 'pointer', userSelect: 'none' }}>
              Advanced: Custom Prompt (optional)
            </summary>
            <div style={{ marginTop: 4 }}>
              <TextArea
                size="small"
                value={heartbeatPrompt}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setHeartbeatPrompt(event.target.value)
                }
                rows={2}
                placeholder="Additional check instructions (e.g. check deploy health, summarize for customer)"
              />
            </div>
          </details>
        )}
      </div>
    </Modal>
  );
}

export function EditTaskModal({
  task,
  members,
  onSaved,
  onPropertiesChanged,
  onCancel,
}: {
  task: api.Task;
  members: ChannelMember[];
  onSaved: () => void;
  // Properties save on their own the moment they are committed, so they refresh
  // the list WITHOUT closing the modal — onSaved would unmount it and discard
  // the title / status / assignee edits the user has not pressed Save on yet.
  onPropertiesChanged: () => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState(task.description);
  const [assigneeId, setAssigneeId] = useState<string | undefined>(task.assignee_id);
  const [status, setStatus] = useState(task.status);
  const [heartbeatEnabled, setHeartbeatEnabled] = useState(
    task.heartbeat_enabled !== false && !!task.heartbeat_interval_ms,
  );
  const [heartbeatMinutes, setHeartbeatMinutes] = useState<number>(
    task.heartbeat_interval_ms ? task.heartbeat_interval_ms / 60000 : DEFAULT_HEARTBEAT_MINUTES,
  );
  const [heartbeatPrompt, setHeartbeatPrompt] = useState(task.heartbeat_prompt ?? '');
  const [saving, setSaving] = useState(false);
  const assigneeOptions = useMemo(() => memberOptions(members), [members]);
  const repositoryMapped = task.properties?.['execution.workspace_source'] === 'repository_mapping';
  const [workspaceSource, setWorkspaceSource] = useState<WorkspaceSource>(
    repositoryMapped ? 'repository_mapping' : 'default',
  );
  const [repository, setRepository] = useState(task.properties?.['execution.repository'] ?? '');
  const [saveError, setSaveError] = useState<string | null>(null);
  const { capability: repositoryCapability, loading: repositoryCapabilityLoading } =
    useRepositoryExecutionOptions(task.channel_id, assigneeId, !repositoryMapped);
  const repositoryConversionSelected =
    !repositoryMapped && workspaceSource === 'repository_mapping';
  const repositoryConversionAllowed =
    task.status === 'open' &&
    status === 'open' &&
    !!assigneeId &&
    !!repositoryCapability?.supported;
  const repositorySelectionValid =
    repositoryConversionAllowed && !!repositoryCapability?.repositories.includes(repository);
  const newHeartbeatMs = heartbeatMinutes * 60 * 1000;
  const editorError = saveError;

  useEffect(() => {
    setSaveError(null);
    if (repositoryMapped) return;
    setWorkspaceSource('default');
    setRepository('');
  }, [assigneeId, repositoryMapped]);

  const handleSave = async () => {
    if (!title.trim() || saving) return;
    if (repositoryConversionSelected && (!repositorySelectionValid || !assigneeId)) return;
    const updates: Record<string, any> = {};
    if (title.trim() !== task.title) updates.title = title.trim();
    if (description.trim() !== task.description) updates.description = description.trim();
    if (assigneeId !== task.assignee_id) updates.assignee_id = assigneeId ?? '';
    if (status !== task.status) updates.status = status;
    // Pausing heartbeat is deliberately non-destructive: preserve interval
    // and prompt, and flip only the scheduling flag.
    if (heartbeatEnabled && newHeartbeatMs !== (task.heartbeat_interval_ms ?? 0)) {
      updates.heartbeat_interval_ms = newHeartbeatMs;
    }
    // A task with no stored interval is unconfigured, not paused; do not
    // invent a disabled configuration while saving unrelated fields.
    if (
      (task.heartbeat_interval_ms ?? 0) > 0 &&
      heartbeatEnabled !== (task.heartbeat_enabled !== false)
    ) {
      updates.heartbeat_enabled = heartbeatEnabled;
    }
    const newPrompt = heartbeatPrompt.trim();
    if (newPrompt !== (task.heartbeat_prompt ?? '')) {
      updates.heartbeat_prompt = newPrompt;
    }
    setSaving(true);
    setSaveError(null);
    try {
      if (Object.keys(updates).length > 0) {
        await api.updateTask(task.id, updates);
      }
      if (repositoryConversionSelected && assigneeId) {
        await api.convertTaskRepositoryMapping(task.id, {
          repository: repository.trim(),
          expected_assignee_id: assigneeId,
        });
      }
      onSaved();
    } catch (error) {
      setSaveError(repositoryMappingError(error));
      setSaving(false);
    }
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Delete Task',
      content: `Are you sure you want to delete task #${task.seq}?`,
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await api.deleteTask(task.id);
        onSaved();
      },
    });
  };

  const footerButtons: ReactNode[] = [
    <Button key="delete" danger icon={<DeleteOutlined />} onClick={handleDelete}>
      Delete
    </Button>,
    <Button key="cancel" onClick={onCancel}>
      Cancel
    </Button>,
    <Button
      key="save"
      type="primary"
      onClick={handleSave}
      disabled={
        !title.trim() ||
        saving ||
        (repositoryConversionSelected && (repositoryCapabilityLoading || !repositorySelectionValid))
      }
    >
      {saving ? 'Saving...' : 'Save'}
    </Button>,
  ];

  return (
    <Modal
      title="Edit Task"
      open
      onOk={handleSave}
      onCancel={onCancel}
      okText={saving ? 'Saving...' : 'Save'}
      okButtonProps={{
        disabled:
          !title.trim() ||
          saving ||
          (repositoryConversionSelected &&
            (repositoryCapabilityLoading || !repositorySelectionValid)),
      }}
      width={420}
      footer={footerButtons}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Title</div>
        <Input
          value={title}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setTitle(event.target.value)}
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Description</div>
        <TextArea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Optional description"
        />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Status</div>
        <Select
          style={{ width: '100%' }}
          value={status}
          onChange={setStatus}
          options={STATUS_COLUMNS.map((c) => ({ value: c.key, label: c.label }))}
        />
      </div>
      <div>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Assignee</div>
        <Select
          aria-label="Assignee"
          style={{ width: '100%' }}
          placeholder="Select assignee"
          allowClear
          value={assigneeId}
          onChange={setAssigneeId}
          options={assigneeOptions}
        />
        {repositoryMapped && (
          <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
            Repository-mapped task stays on its selected computer. You may choose another assignee
            on that computer or clear the assignee.
          </div>
        )}
      </div>
      {(assigneeId || repositoryMapped) && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Execution Workspace</div>
          <RepositoryWorkspaceSelector
            workspaceSource={workspaceSource}
            onWorkspaceSourceChange={setWorkspaceSource}
            repository={repository}
            onRepositoryChange={setRepository}
            capability={repositoryCapability}
            loading={repositoryCapabilityLoading}
            repositoryMapped={repositoryMapped}
            selectionAllowed={task.status === 'open' && status === 'open' && !!assigneeId}
          />
          {!repositoryMapped && task.status !== 'open' && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              Repository mapping can only be added before the task starts.
            </div>
          )}
        </div>
      )}
      {editorError && (
        <div role="alert" style={{ color: '#ff4d4f', fontSize: 12, marginTop: 12 }}>
          {editorError}
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>Heartbeat</span>
          <Switch size="small" checked={heartbeatEnabled} onChange={setHeartbeatEnabled} />
        </div>
        {heartbeatEnabled && (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={heartbeatMinutes}
            onChange={setHeartbeatMinutes}
            options={HEARTBEAT_INTERVAL_OPTIONS}
          />
        )}
        {heartbeatEnabled && (
          <details style={{ marginTop: 8 }} open={!!heartbeatPrompt}>
            <summary style={{ fontSize: 12, color: '#888', cursor: 'pointer', userSelect: 'none' }}>
              Periodic check-in prompt (optional)
            </summary>
            <div style={{ marginTop: 4 }}>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
                Sent to the assignee each time this task's heartbeat runs. It replaces the default
                “Heartbeat: status update requested” check-in message and does not change the task
                description or instructions.
              </div>
              <TextArea
                size="small"
                aria-label="Periodic check-in prompt"
                value={heartbeatPrompt}
                onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
                  setHeartbeatPrompt(event.target.value)
                }
                rows={2}
                placeholder="e.g. Check deploy health and summarize the current status"
              />
            </div>
          </details>
        )}
      </div>
      <TaskPropertiesEditor task={task} onChanged={onPropertiesChanged} />
    </Modal>
  );
}
